import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../../../core/services/api.service';
import { S3ImageUploadService } from '../../../../core/services/s3-image-upload.service';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { StorybookPreviewModal } from './storybook-preview-modal/storybook-preview-modal';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import {
  Creator,
  FamilyMember,
  FamilySpace,
  StoryCategory,
  StoryItem,
  StoryListingData,
  StoryListingRequest,
  StoryMasterData,
  StoryType,
} from '../../../../core/models/story.models';
import {
  CreateStorybookRequest,
  CreateStorybookResponse,
  ExportStorybookRequest,
  ExportStorybookResponse,
  GenerateShareableQRCodeResponse,
  StorybookDetail,
  StorybookExportType,
  StorybookPreview,
  StorybookStoryIdEntry,
  UpdateStorybookRequest,
} from '../../../../core/models/storybook.models';

/** A story listing card plus local (client-only) selection state. */
interface SelectableStory extends StoryItem {
  isSelected: boolean;
}

const PAGE_SIZE = 20;
const VIEW_MODE_KEY = 'fm_create_storybook_view_mode';
const MAX_COVER_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_COVER_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** One narrative page + its paired quote/QR page — the building block cycled to fill DUMMY_STORYBOOK_PREVIEW. */
const DUMMY_PAGE_TEMPLATES: Array<{
  title: string;
  description: string;
  mediaType: string;
  qrCodeUrl: string;
  image: string;
}> = [
  {
    title: 'Early Years',
    description:
      'Every story has a beginning. These early moments shaped who I am today. Filled with wonder, curiosity, family, and little adventures that left a big impact.',
    mediaType: 'photo',
    qrCodeUrl: '',
    image: 'images/people-1.png',
  },
  {
    title: 'Grandpa’s Bedtime Stories',
    description: 'The best lessons in life are learned from the people who love us most.',
    mediaType: 'video',
    qrCodeUrl: 'images/icons/fm-qr.svg',
    image: 'images/stories/stories-post-1.png',
  },
  {
    title: 'Career & Work',
    description:
      'Every job taught a lesson, every challenge built resilience. This chapter looks back at the work that shaped a lifetime of purpose.',
    mediaType: 'photo',
    qrCodeUrl: '',
    image: 'images/stories/stories-post-2.png',
  },
  {
    title: 'A Family Vacation',
    description: 'Sun, sand, and laughter — the trip everyone still talks about.',
    mediaType: 'video',
    qrCodeUrl: 'images/icons/fm-qr.svg',
    image: 'images/stories/stories-post-3.png',
  },
];

/**
 * Temporary stand-in for GET /Storybooks/PreviewStorybook/{storybookId} — the
 * backend currently returns placeholder values, so the preview modal is
 * populated with this (28 pages, cycling the 4 templates above) until real
 * page content is wired up. Remove once the API returns actual generated pages.
 */
function buildDummyStorybookPreview(): StorybookPreview {
  const totalPages = 28;
  const now = new Date().toISOString();
  const pages = Array.from({ length: totalPages }, (_, i) => {
    const t = DUMMY_PAGE_TEMPLATES[i % DUMMY_PAGE_TEMPLATES.length];
    return {
      pageId: i + 1,
      storyId: `s${Math.floor(i / 2) + 1}`,
      pageOrder: i,
      title: t.title,
      description: t.description,
      memoryDate: now,
      mediaType: t.mediaType,
      qrCodeUrl: t.qrCodeUrl,
      media: [{ mediaId: i + 1, mediaUrl: t.image, mediaType: t.mediaType, displayOrder: 1 }],
    };
  });

  return {
    storybookId: 'dummy',
    title: 'Life’s Journey',
    description: 'A heartfelt collection of memories.',
    coverImageUrl: 'images/storybooks/bookcover-1.png',
    authorName: 'You',
    memoryDate: now,
    totalPages,
    pages,
  };
}

const DUMMY_STORYBOOK_PREVIEW: StorybookPreview = buildDummyStorybookPreview();

/**
 * Set to false once GET /Storybooks/PreviewStorybook/{storybookId} returns
 * real generated pages — the API is still called either way (for the
 * toaster/console verification), this only controls which data populates
 * the preview modal.
 */
const USE_DUMMY_PREVIEW_DATA = true;

@Component({
  selector: 'app-create-storybooks',
  templateUrl: './create-storybooks.html',
  styleUrl: './create-storybooks.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TooltipDirective, StorybookPreviewModal],
})
export class CreateStorybooks implements OnInit, OnDestroy {
  // ── Dependency injection ───────────────────────────────────────────────────
  private readonly api = inject(ApiService);
  private readonly s3 = inject(S3ImageUploadService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone = inject(NgZone);

  // ── UI state ───────────────────────────────────────────────────────────────
  readonly isInitializing = signal(true);
  readonly hasError = signal(false);
  readonly isFilterPanelOpen = signal(false);
  readonly viewMode = signal<'grid' | 'list'>(this.readViewMode());

  // ── Server data ────────────────────────────────────────────────────────────
  readonly stories = signal<SelectableStory[]>([]);
  readonly totalRecords = signal(0);
  readonly hasMoreStories = signal(false);
  readonly isLoadingMore = signal(false);
  readonly hasStoriesInSystem = signal(false);

  /**
   * IDs of every story selected so far, in the order they were picked —
   * kept across pagination and filter changes. Order matters: it's sent to
   * the backend as storyIdList's sortOrder, which is the sequence the AI
   * lays out pages in. An ordered array (not a Set) is required so that
   * selecting/deselecting/reselecting always keeps the remaining picks in
   * their original relative order.
   */
  private readonly selectedStoryIds = signal<string[]>([]);
  readonly selectedStoriesCount = computed(() => this.selectedStoryIds().length);

  readonly categories = signal<StoryCategory[]>([]);
  readonly familySpaces = signal<FamilySpace[]>([]);
  readonly familyMembers = signal<FamilyMember[]>([]);
  readonly creators = signal<Creator[]>([]);

  // ── Active filter state ────────────────────────────────────────────────────
  customSearch = '';
  readonly submittedSearch = signal('');
  readonly activeCategoryId = signal<number | null>(null);
  readonly activeViewType = signal<string | null>(null);
  readonly activeFamilySpaceId = signal<string | null>(null);
  readonly activeTaggedPersonIds = signal<string[]>([]);
  readonly activeCreatorIds = signal<string[]>([]);
  readonly pageNumber = signal(1);

  readonly viewTypeOptions: { label: string; value: string }[] = [
    { label: 'All', value: '' },
    { label: 'My Stories', value: '1' },
    { label: 'Family Stories', value: '2' },
    { label: 'Shared Stories', value: '3' },
  ];

  // ── Derived filter lists ───────────────────────────────────────────────────
  readonly filteredFamilyMembers = computed(() => {
    const familyId = this.activeFamilySpaceId();
    if (!familyId) return this.familyMembers();
    return this.familyMembers().filter((m) => m.familySpaceId === familyId);
  });

  readonly filteredCreators = computed(() => {
    const familyId = this.activeFamilySpaceId();
    if (!familyId) return this.creators();

    const byFamilySpace = this.creators().filter((c) => c.familySpaceId === familyId);
    if (byFamilySpace.length > 0) return byFamilySpace;

    const familyUserIds = new Set(
      this.familyMembers()
        .filter((m) => m.familySpaceId === familyId && !!m.userId)
        .map((m) => m.userId as string),
    );
    if (familyUserIds.size > 0) {
      const byUserId = this.creators().filter((c) => familyUserIds.has(c.userId));
      if (byUserId.length > 0) return byUserId;
    }

    return this.creators();
  });

  // ── Private ────────────────────────────────────────────────────────────────
  private isRequestInFlight = false;
  private scrollObserver?: IntersectionObserver;

  // ────────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ────────────────────────────────────────────────────────────────────────────

  async ngOnInit(): Promise<void> {
    // Edit route ('storybooks/edit/:storybookId') — load the existing
    // storybook and populate the form fields so the user can change them.
    // Awaited (unlike the query-param restore below) so selectedStoryIds is
    // set BEFORE initPage()'s loadStories() runs — otherwise the first page
    // of stories would render with nothing pre-selected.
    const editStorybookId = this.route.snapshot.paramMap.get('storybookId');
    if (editStorybookId) {
      this.isEditMode.set(true);
      this.createdStorybookId.set(editStorybookId);
      await this.loadStorybookDetail(editStorybookId, true);
    } else {
      // Restores the "already created" state across a page refresh — the
      // storybookId is stamped onto the URL (query param) right after a
      // successful create.
      const existingStorybookId = this.route.snapshot.queryParamMap.get('storybookId');
      if (existingStorybookId) {
        this.createdStorybookId.set(existingStorybookId);
        this.loadStorybookDetail(existingStorybookId);
      }
    }
    await this.initPage();
  }

  ngOnDestroy(): void {
    this.scrollObserver?.disconnect();
  }

  private async initPage(): Promise<void> {
    await this.loadMasterData();
    await this.loadStories();
  }

  private async loadMasterData(): Promise<void> {
    try {
      const resp = await this.api.request<StoryMasterData>(
        'GET',
        API_ROUTES.GET_STORY_MASTER_DATA,
        undefined,
        { showToaster: false, showLoader: false },
      );
      if (resp.status && resp.data) {
        const d = resp.data;
        this.categories.set(d.categories ?? d.activeCategories ?? []);
        this.familySpaces.set(d.familySpaces ?? d.activeFamilyList ?? d.activeFamilies ?? []);
        this.familyMembers.set(d.familyMembers ?? d.familyMemberList ?? []);
        this.creators.set(d.creators ?? d.creatorList ?? d.allCreatorList ?? []);
      }
    } catch (err) {
      console.error('[CreateStorybooks] Error loading master data:', err);
    }
  }

  /**
   * Fetches a page of stories to pick from via POST /Story/GetStoryListing.
   * Pages are fetched PAGE_SIZE at a time and appended as the user scrolls,
   * so this stays fast even when the family has hundreds of stories.
   * Selection (selectedStoryIds) is tracked independently of the page
   * contents, so switching filters/pages never loses what was picked.
   */
  async loadStories(isLoadMore = false): Promise<void> {
    if (this.isRequestInFlight) return;

    this.isRequestInFlight = true;
    if (isLoadMore) {
      this.isLoadingMore.set(true);
    } else {
      this.hasError.set(false);
    }

    const viewType = this.activeViewType();

    const request: StoryListingRequest = {
      pageNumber: this.pageNumber(),
      pageSize: PAGE_SIZE,
      searchText: this.submittedSearch(),
      categoryId: this.activeCategoryId(),
      storyType: viewType !== null ? (Number(viewType) as StoryType) : null,
      familySpaceId: this.activeFamilySpaceId(),
      creatorIds: this.activeCreatorIds().length ? this.activeCreatorIds() : null,
      taggedPersonIds: this.activeTaggedPersonIds().length ? this.activeTaggedPersonIds() : null,
      sortColumn: null,
      sortDirection: null,
    };

    try {
      const resp = await this.api.request<StoryListingData>(
        'POST',
        API_ROUTES.GET_STORY_LISTING,
        request,
        { showToaster: false, showLoader: false, useToken: true },
      );

      if (resp.status && resp.data) {
        const selectedIds = this.selectedStoryIds();
        const incoming: SelectableStory[] = (resp.data.stories ?? []).map((story) => ({
          ...story,
          isSelected: selectedIds.includes(story.storyId),
        }));
        const total = resp.data.totalRecords ?? 0;

        if (isLoadMore) {
          this.stories.update((existing) => [...existing, ...incoming]);
        } else {
          this.stories.set(incoming);
        }

        this.totalRecords.set(total);
        if (total > 0) this.hasStoriesInSystem.set(true);
        this.hasMoreStories.set(this.stories().length < total);
      } else {
        if (!isLoadMore) {
          this.stories.set([]);
          this.totalRecords.set(0);
          this.hasError.set(true);
        }
        this.hasMoreStories.set(false);
      }
    } catch {
      if (!isLoadMore) {
        this.hasError.set(true);
        this.stories.set([]);
        this.totalRecords.set(0);
      }
      this.hasMoreStories.set(false);
    } finally {
      this.isLoadingMore.set(false);
      this.isRequestInFlight = false;
      this.isInitializing.set(false);

      if (!isLoadMore && isPlatformBrowser(this.platformId)) {
        setTimeout(() => this.setupScrollObserver(), 100);
      }
    }
  }

  private setupScrollObserver(): void {
    this.scrollObserver?.disconnect();
    const sentinel = document.querySelector('#storiesScrollSentinel');
    if (!sentinel) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isRequestInFlight && this.hasMoreStories()) {
          this.zone.run(() => {
            this.pageNumber.update((n) => n + 1);
            this.loadStories(true);
          });
        }
      },
      { threshold: 0.1 },
    );
    this.scrollObserver.observe(sentinel);
  }

  // ────────────────────────────────────────────────────────────────────────────
  // User interaction handlers
  // ────────────────────────────────────────────────────────────────────────────

  toggleFilterPanel(): void {
    this.isFilterPanelOpen.update((open) => !open);
  }

  async onSearch(): Promise<void> {
    const term = this.customSearch?.trim() ?? '';
    this.customSearch = term;
    this.submittedSearch.set(term);
    this.pageNumber.set(1);
    await this.loadStories();
  }

  async clearSearch(): Promise<void> {
    this.customSearch = '';
    this.submittedSearch.set('');
    this.pageNumber.set(1);
    await this.loadStories();
  }

  setCategory(categoryId: number | null): void {
    this.activeCategoryId.set(categoryId);
    this.pageNumber.set(1);
    this.loadStories();
  }

  onViewTypeChange(value: string): void {
    this.activeViewType.set(value === '' ? null : value);
    this.pageNumber.set(1);
    this.loadStories();
  }

  onFamilySpaceChange(value: string): void {
    const familyId = value === '' ? null : value;
    this.activeFamilySpaceId.set(familyId);

    if (familyId) {
      const validMemberIds = new Set(
        this.familyMembers()
          .filter((m) => m.familySpaceId === familyId)
          .map((m) => m.familyMemberId),
      );
      this.activeTaggedPersonIds.update((ids) => ids.filter((id) => validMemberIds.has(id)));

      const familyUserIds = new Set(
        this.familyMembers()
          .filter((m) => m.familySpaceId === familyId && !!m.userId)
          .map((m) => m.userId as string),
      );
      if (familyUserIds.size > 0) {
        this.activeCreatorIds.update((ids) => ids.filter((id) => familyUserIds.has(id)));
      }
    }

    this.pageNumber.set(1);
    this.loadStories();
  }

  toggleTaggedPerson(id: string): void {
    this.activeTaggedPersonIds.update((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );
    this.pageNumber.set(1);
    this.loadStories();
  }

  toggleCreator(id: string): void {
    this.activeCreatorIds.update((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );
    this.pageNumber.set(1);
    this.loadStories();
  }

  clearAllFilters(): void {
    this.customSearch = '';
    this.submittedSearch.set('');
    this.activeCategoryId.set(null);
    this.activeViewType.set(null);
    this.activeFamilySpaceId.set(null);
    this.activeTaggedPersonIds.set([]);
    this.activeCreatorIds.set([]);
    this.pageNumber.set(1);
    this.loadStories();
  }

  setViewMode(mode: 'grid' | 'list'): void {
    this.viewMode.set(mode);
    this.writeViewMode(mode);
  }

  toggleStorySelection(story: SelectableStory): void {
    story.isSelected = !story.isSelected;
    this.selectedStoryIds.update((ids) => {
      if (story.isSelected) {
        return [...ids, story.storyId];
      }
      return ids.filter((id) => id !== story.storyId);
    });
    // Force the stories array to a new reference so OnPush picks up the mutated card.
    this.stories.update((list) => [...list]);
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Template utility helpers
  // ────────────────────────────────────────────────────────────────────────────

  get hasAnyActiveFilter(): boolean {
    return (
      !!this.submittedSearch() ||
      this.activeCategoryId() !== null ||
      this.activeViewType() !== null ||
      this.activeFamilySpaceId() !== null ||
      this.activeTaggedPersonIds().length > 0 ||
      this.activeCreatorIds().length > 0
    );
  }

  get hasActiveFilters(): boolean {
    const creatorSet = new Set(this.creators().map((c) => c.userId));
    const hasValidCreator = this.activeCreatorIds().some((id) => creatorSet.has(id));
    return (
      this.activeCategoryId() !== null ||
      this.activeViewType() !== null ||
      this.activeFamilySpaceId() !== null ||
      this.activeTaggedPersonIds().length > 0 ||
      hasValidCreator
    );
  }

  getActiveFamilyName(): string {
    const id = this.activeFamilySpaceId();
    if (!id) return '';
    return this.familySpaces().find((f) => f.familySpaceId === id)?.familyName ?? '';
  }

  getActiveCategoryName(): string {
    const id = this.activeCategoryId();
    if (id === null) return '';
    return this.categories().find((c) => c.categoryId === id)?.categoryName ?? '';
  }

  getViewTypeName(): string {
    const vt = this.activeViewType();
    const option = this.viewTypeOptions.find((o) => o.value === vt);
    return option ? option.label : '';
  }

  /** Formats a duration value in seconds to "M:SS" display string. */
  formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  getAuthorInitials(name: string | undefined): string {
    if (!name?.trim()) return '?';
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('');
  }

  private readViewMode(): 'grid' | 'list' {
    if (isPlatformBrowser(this.platformId)) {
      const saved = localStorage.getItem(VIEW_MODE_KEY);
      if (saved === 'grid' || saved === 'list') return saved;
    }
    return 'grid';
  }

  private writeViewMode(mode: 'grid' | 'list'): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    }
  }

  // -- Date picker -----------------------------------------------------------
  readonly memoryDate = signal<string>('');
  readonly showDatePicker = signal(false);
  readonly pickerDropUp = signal(false);
  readonly pickerViewYear = signal(new Date().getFullYear());
  readonly pickerViewMonth = signal(new Date().getMonth());

  private static readonly MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  readonly pickerMonthLabel = computed(
    () => `${CreateStorybooks.MONTH_NAMES[this.pickerViewMonth()]} ${this.pickerViewYear()}`,
  );

  readonly memoryDateDisplay = computed(() => {
    const val = this.memoryDate();
    if (!val) return '';
    const parts = val.split('-');
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (!y || !m || !d) return val;
    return `${CreateStorybooks.MONTH_NAMES[m - 1]} ${d}, ${y}`;
  });

  readonly pickerCalendarDays = computed(() => {
    const year = this.pickerViewYear();
    const month = this.pickerViewMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selVal = this.memoryDate();
    const selected = selVal ? new Date(selVal + 'T00:00:00') : null;

    type DayCell = {
      date: Date | null;
      day: number | null;
      isCurrentMonth: boolean;
      isToday: boolean;
      isSelected: boolean;
      isDisabled: boolean;
    };
    const cells: DayCell[] = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push({
        date: null,
        day: null,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        isDisabled: true,
      });
    }
    for (let d = 1; d <= totalDays; d++) {
      const date = new Date(year, month, d);
      date.setHours(0, 0, 0, 0);
      cells.push({
        date,
        day: d,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
        isSelected: selected ? date.getTime() === selected.getTime() : false,
        isDisabled: false, // allow past/future dates for memories
      });
    }
    while (cells.length % 7 !== 0) {
      cells.push({
        date: null,
        day: null,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        isDisabled: true,
      });
    }
    return cells;
  });

  openDatePicker(event: MouseEvent): void {
    const val = this.memoryDate();
    if (val) {
      const [y, m] = val.split('-').map(Number);
      this.pickerViewYear.set(y);
      this.pickerViewMonth.set(m - 1);
    }
    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    this.pickerDropUp.set(spaceBelow < 320 && rect.top > 320);
    this.showDatePicker.set(true);
  }

  closeDatePicker(): void {
    this.showDatePicker.set(false);
  }

  selectPickerDate(date: Date | null): void {
    if (!date) return;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const value = `${y}-${m}-${d}`;
    this.memoryDate.set(value);
    this.showDatePicker.set(false);
  }

  pickerPrevMonth(): void {
    if (this.pickerViewMonth() === 0) {
      this.pickerViewMonth.set(11);
      this.pickerViewYear.update((y) => y - 1);
    } else {
      this.pickerViewMonth.update((v) => v - 1);
    }
  }

  pickerNextMonth(): void {
    if (this.pickerViewMonth() === 11) {
      this.pickerViewMonth.set(0);
      this.pickerViewYear.update((y) => y + 1);
    } else {
      this.pickerViewMonth.update((v) => v + 1);
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Storybook Details — title / description / cover image
  // ────────────────────────────────────────────────────────────────────────────

  readonly title = signal('');
  readonly description = signal('');
  readonly titleError = signal('');
  readonly storiesError = signal('');

  readonly coverImageUrl = signal('');
  readonly coverImagePreviewUrl = signal('');
  readonly coverImageFileName = signal('');
  readonly isUploadingCover = signal(false);
  readonly coverImageError = signal('');

  readonly isSubmitting = signal(false);

  readonly isSubmitDisabled = computed(() => this.isUploadingCover() || this.isSubmitting());

  /** Set once POST /Storybooks succeeds — hides the "Create Storybook" card. */
  readonly createdStorybookId = signal<string | null>(null);
  readonly isCreated = computed(() => this.createdStorybookId() !== null);

  /** True when this page was opened via the 'storybooks/edit/:storybookId' route. */
  readonly isEditMode = signal(false);

  /** Populated from GET /Storybooks/GetStorybookDetail/{storybookId} once created. */
  readonly storybookDetail = signal<StorybookDetail | null>(null);
  readonly isLoadingDetail = signal(false);

  /** Export (Download PDF / Export DOCX) — tracked per type so only the clicked button spins. */
  readonly exportingType = signal<StorybookExportType | null>(null);
  readonly StorybookExportType = StorybookExportType;

  /** Shareable QR code — generated on demand once the storybook exists. */
  readonly isGeneratingQrCode = signal(false);
  readonly qrCodeUrl = signal('');
  readonly shareableLink = signal('');

  readonly isLoadingPreview = signal(false);
  readonly previewData = signal<StorybookPreview | null>(null);

  private extractFileNameFromUrl(url: string): string {
    const withoutQuery = url.split('?')[0];
    return withoutQuery.substring(withoutQuery.lastIndexOf('/') + 1) || 'Cover image';
  }

  private validateCoverImageFile(file: File): string | null {
    if (!ALLOWED_COVER_IMAGE_TYPES.includes(file.type)) {
      return 'Only JPG, PNG, and WEBP images are allowed.';
    }
    if (file.size > MAX_COVER_IMAGE_BYTES) {
      return 'Image must be smaller than 5 MB.';
    }
    return null;
  }

  async onCoverImageSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    const err = this.validateCoverImageFile(file);
    if (err) {
      this.coverImageError.set(err);
      return;
    }

    this.coverImageError.set('');
    this.coverImagePreviewUrl.set(URL.createObjectURL(file));
    this.isUploadingCover.set(true);

    try {
      const result = await this.s3.upload(file, 'storybookCover');
      // API expects just the generated S3 file name (e.g. "1789116516800.webp"), not the full URL.
      this.coverImageUrl.set(result.fileName);
      this.coverImageFileName.set(file.name);
    } catch {
      this.coverImageError.set('Cover image upload failed. Please try again.');
      this.coverImagePreviewUrl.set('');
      this.coverImageUrl.set('');
      this.coverImageFileName.set('');
    } finally {
      this.isUploadingCover.set(false);
    }
  }

  removeCoverImage(): void {
    this.coverImagePreviewUrl.set('');
    this.coverImageUrl.set('');
    this.coverImageFileName.set('');
    this.coverImageError.set('');
  }

  /**
   * Creates the storybook via POST /Storybooks with the selected stories'
   * IDs. On success the "Create Storybook" card is hidden and the Book
   * Preview card is populated from GET /Storybooks/GetStorybookDetail —
   * every other action (preview, download, export, print) only makes
   * sense once that backend data exists.
   */
  async createStorybook(): Promise<void> {
    const title = this.title().trim();
    let hasValidationError = false;

    if (!title) {
      this.titleError.set('Storybook title is required.');
      hasValidationError = true;
    } else {
      this.titleError.set('');
    }

    if (this.selectedStoriesCount() === 0) {
      this.storiesError.set('Select at least one story to include in the storybook.');
      hasValidationError = true;
    } else {
      this.storiesError.set('');
    }

    if (hasValidationError || this.isUploadingCover() || this.isSubmitting()) return;

    this.isSubmitting.set(true);

    const request: CreateStorybookRequest = {
      title,
      description: this.description().trim(),
      coverImageUrl: this.coverImageUrl(),
      memoryDate: this.memoryDate(),
      storyIdList: this.buildStoryIdList(),
    };

    try {
      const resp = await this.api.request<CreateStorybookResponse>(
        'POST',
        API_ROUTES.STORY_BOOK_CREATE,
        request,
        { showToaster: true, showLoader: true, useToken: true },
      );

      if (resp.status && resp.data?.storybookId) {
        this.createdStorybookId.set(resp.data.storybookId);
        // Stamp the id onto the URL so a page refresh restores this "already
        // created" state instead of resetting to the blank create form.
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { storybookId: resp.data.storybookId },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
        await this.loadStorybookDetail(resp.data.storybookId);
      }
    } finally {
      this.isSubmitting.set(false);
    }
  }

  /**
   * Saves title/description/cover/date changes, plus the current story
   * selection/order, via PUT /Storybooks/UpdateStorybook (edit route only).
   */
  async updateStorybook(): Promise<void> {
    const storybookId = this.createdStorybookId();
    if (!storybookId) return;

    const title = this.title().trim();
    if (!title) {
      this.titleError.set('Storybook title is required.');
      return;
    }
    this.titleError.set('');

    if (this.isUploadingCover() || this.isSubmitting()) return;

    this.isSubmitting.set(true);

    const request: UpdateStorybookRequest = {
      storybookId,
      title,
      description: this.description().trim(),
      coverImageUrl: this.coverImageUrl(),
      memoryDate: this.memoryDate(),
      storyIdList: this.buildStoryIdList(),
    };

    try {
      const resp = await this.api.request<unknown>(
        'POST',
        API_ROUTES.STORY_BOOK_UPDATE,
        request,
        { showToaster: true, showLoader: true, useToken: true },
      );

      if (resp.status) {
        await this.loadStorybookDetail(storybookId);
      }
    } finally {
      this.isSubmitting.set(false);
    }
  }

  /**
   * Loads the full generator payload via GET /Storybooks/GetStorybookDetail/{storybookId}.
   * When populateForm is true (edit route) it also fills the Storybook
   * Details form fields and pre-selects the stories already in the book,
   * so the page opens ready to edit instead of blank.
   */
  private async loadStorybookDetail(storybookId: string, populateForm = false): Promise<void> {
    this.isLoadingDetail.set(true);
    try {
      const resp = await this.api.request<StorybookDetail>(
        'GET',
        `${API_ROUTES.STORY_BOOK_GET_DETAIL}/${storybookId}`,
        undefined,
        // Show the loader for the edit route's primary load (populateForm),
        // but not for the quieter post-create/post-update background refreshes.
        { showToaster: false, showLoader: populateForm, useToken: true },
      );
      if (resp.status && resp.data) {
        this.storybookDetail.set(resp.data);
        if (populateForm) {
          this.populateFormFromDetail(resp.data);
        }
      }
    } finally {
      this.isLoadingDetail.set(false);
    }
  }

  /**
   * Fills the Storybook Details form and pre-selects the included stories
   * (in their saved sortOrder) from a loaded StorybookDetail — used when
   * opening the edit route, so the picker starts exactly as last saved.
   */
  private populateFormFromDetail(detail: StorybookDetail): void {
    this.title.set(detail.title ?? '');
    this.description.set(detail.description ?? '');
    this.coverImageUrl.set(detail.coverImageUrl ?? '');
    this.coverImagePreviewUrl.set(detail.coverImageUrl ?? '');
    this.coverImageFileName.set(
      detail.coverImageUrl ? this.extractFileNameFromUrl(detail.coverImageUrl) : '',
    );
    this.memoryDate.set(detail.memoryDate ? detail.memoryDate.split('T')[0] : '');
    const orderedIds = [...(detail.storyIdList ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((entry) => entry.storyId);
    this.selectedStoryIds.set(orderedIds);
  }

  /** Builds the storyIdList payload from the current selection — array position becomes sortOrder. */
  private buildStoryIdList(): StorybookStoryIdEntry[] {
    return this.selectedStoryIds().map((storyId, sortOrder) => ({ storyId, sortOrder }));
  }

  /** Exports the created storybook via POST /Storybooks/ExportStorybook. */
  async exportStorybook(exportType: StorybookExportType): Promise<void> {
    const storybookId = this.createdStorybookId();
    if (!storybookId || this.exportingType() !== null) return;

    this.exportingType.set(exportType);

    const request: ExportStorybookRequest = { storybookId, exportType };

    try {
      const resp = await this.api.request<ExportStorybookResponse>(
        'POST',
        API_ROUTES.STORY_BOOK_EXPORT,
        request,
        { showToaster: true, showLoader: true, useToken: true },
      );

      if (resp.status && resp.data?.fileBytes) {
        const ext = exportType === StorybookExportType.Pdf ? '.pdf' : '.docx';
        const mimeType =
          exportType === StorybookExportType.Pdf
            ? 'application/pdf'
            : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        const fileName = resp.data.fileName.endsWith(ext)
          ? resp.data.fileName
          : `${resp.data.fileName}${ext}`;
        this.downloadBase64File(resp.data.fileBytes, fileName, mimeType);
      }
    } finally {
      this.exportingType.set(null);
    }
  }

  /** Decodes a base64 file payload and triggers a browser download for it. */
  private downloadBase64File(base64: string, fileName: string, mimeType: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  /** Generates a shareable QR code for the created storybook. */
  async generateQrCode(): Promise<void> {
    const storybookId = this.createdStorybookId();
    if (!storybookId || this.isGeneratingQrCode()) return;

    this.isGeneratingQrCode.set(true);

    try {
      const resp = await this.api.request<GenerateShareableQRCodeResponse>(
        'GET',
        `${API_ROUTES.STORY_BOOK_GENERATE_QR_CODE}/${storybookId}`,
        undefined,
        { showToaster: true, showLoader: true, useToken: true },
      );

      if (resp.status && resp.data) {
        this.qrCodeUrl.set(resp.data.qrCodeUrl);
        this.shareableLink.set(resp.data.shareableLink);
      }
    } finally {
      this.isGeneratingQrCode.set(false);
    }
  }

  /**
   * Calls GET /Storybooks/PreviewStorybook/{storybookId} and opens the
   * preview modal. USE_DUMMY_PREVIEW_DATA controls whether the modal shows
   * DUMMY_STORYBOOK_PREVIEW or the real resp.data — the API call itself
   * always happens so the toaster/console reflect whether it's working.
   */
  async previewStorybook(): Promise<void> {
    const storybookId = this.createdStorybookId();
    if (!storybookId || this.isLoadingPreview()) return;

    this.isLoadingPreview.set(true);

    try {
      const resp = await this.api.request<StorybookPreview>(
        'GET',
        `${API_ROUTES.STORY_BOOK_PREVIEW}/${storybookId}`,
        undefined,
        { showToaster: false, showLoader: true, useToken: true },
      );

      console.log('[CreateStorybooks] PreviewStorybook response:', resp);

      if (USE_DUMMY_PREVIEW_DATA) {
        this.previewData.set(DUMMY_STORYBOOK_PREVIEW);
      } else if (resp.status && resp.data) {
        this.previewData.set(resp.data);
      }
    } finally {
      this.isLoadingPreview.set(false);
    }
  }

  closePreview(): void {
    this.previewData.set(null);
  }
}
